import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, FlaskConical, CheckCircle, Apple, Sparkles } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useSuggestions } from '../hooks/useSuggestions';
import type { ProductType } from '../types';

interface IngredientReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  productType: ProductType;
  productName?: string;
}

export function IngredientReportModal({
  isOpen,
  onClose,
  productType: initialProductType,
  productName: initialProductName,
}: IngredientReportModalProps) {
  const { t } = useTranslation('scanner');
  const { submitIngredientReport, isSubmitting, error, clearError } = useSuggestions();
  const [success, setSuccess] = useState(false);

  const [ingredientName, setIngredientName] = useState('');
  const [productType, setProductType] = useState<ProductType>(initialProductType);
  const [foundInProduct, setFoundInProduct] = useState(initialProductName || '');
  const [notes, setNotes] = useState('');

  const handleSubmit = async () => {
    clearError();

    if (!ingredientName.trim()) {
      return;
    }

    const result = await submitIngredientReport({
      ingredient_name: ingredientName.trim(),
      product_type: productType,
      found_in_product: foundInProduct.trim() || undefined,
      notes: notes.trim() || undefined,
    });

    if (result) {
      setSuccess(true);
    }
  };

  const handleClose = () => {
    setIngredientName('');
    setProductType(initialProductType);
    setFoundInProduct(initialProductName || '');
    setNotes('');
    setSuccess(false);
    clearError();
    onClose();
  };

  if (!isOpen) return null;

  // Success view
  if (success) {
    const successContent = (
      <>
        <div className="bottom-sheet-backdrop" onClick={handleClose} />
        <div className="bottom-sheet-container">
          <div className="bottom-sheet-modal">
            <div className="bottom-sheet-header">
              <h2>{t('ingredientReport.successTitle')}</h2>
              <button
                onClick={handleClose}
                className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bottom-sheet-content text-center">
              <div className="w-16 h-16 bg-pp-teal/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-pp-teal" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                {t('ingredientReport.successMessage')}
              </h3>
              <p className="text-white/70">
                {t('ingredientReport.successDescription')}
              </p>
            </div>

            <div className="bottom-sheet-footer">
              <button
                onClick={handleClose}
                className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90"
              >
                {t('ingredientReport.close')}
              </button>
            </div>
          </div>
        </div>
      </>
    );
    return createPortal(successContent, document.body);
  }

  const modalContent = (
    <>
      <div className="bottom-sheet-backdrop" onClick={handleClose} />
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-pp-dark/25 rounded-xl flex items-center justify-center">
                <FlaskConical className="w-5 h-5 text-pp-orange" />
              </div>
              <h2 className="!text-lg !font-semibold text-white">
                {t('ingredientReport.title')}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content">
            <div className="space-y-4">
              <p className="text-sm text-white/70">
                {t('ingredientReport.description')}
              </p>

              {/* Product Type Slider */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  {t('ingredientReport.productTypeLabel')}
                </label>
                <div className="glass-effect rounded-[15px] p-1 relative">
                  <div
                    className={`absolute top-1 bottom-1 w-[calc(50%-0.25rem)] bg-pp-orange rounded-[15px] transition-transform duration-300 ease-out ${
                      productType === 'cosmetic' ? 'translate-x-[calc(100%+0.25rem)]' : 'translate-x-0'
                    }`}
                  />
                  <div className="relative flex">
                    <button
                      type="button"
                      onClick={() => setProductType('food')}
                      className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-[15px] transition-colors z-10"
                    >
                      <Apple className="w-4 h-4 text-white" />
                      <span className="font-medium text-white text-sm">{t('ingredientReport.food')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setProductType('cosmetic')}
                      className="flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-[15px] transition-colors z-10"
                    >
                      <Sparkles className="w-4 h-4 text-white" />
                      <span className="font-medium text-white text-sm">{t('ingredientReport.cosmetic')}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Ingredient Name */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('ingredientReport.nameLabel')}
                </label>
                <input
                  type="text"
                  value={ingredientName}
                  onChange={(e) => setIngredientName(e.target.value)}
                  placeholder={t('ingredientReport.namePlaceholder')}
                  className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange"
                />
              </div>

              {/* Found in Product */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('ingredientReport.productLabel')}
                </label>
                <input
                  type="text"
                  value={foundInProduct}
                  onChange={(e) => setFoundInProduct(e.target.value)}
                  placeholder={t('ingredientReport.productPlaceholder')}
                  className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-white/70 mb-1">
                  {t('ingredientReport.additionalLabel')}
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('ingredientReport.additionalPlaceholder')}
                  rows={3}
                  className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 resize-none focus:outline-none focus:ring-2 focus:ring-pp-orange"
                />
              </div>

              {error && (
                <div className="bg-red-500/20 text-red-300 px-3 py-2 rounded-xl text-sm border border-red-500/30">
                  {error}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bottom-sheet-footer">
            <button
              onClick={handleClose}
              className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
            >
              {t('ingredientReport.cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !ingredientName.trim()}
              className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium flex items-center justify-center hover:bg-pp-orange/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <FlaskConical className="w-4 h-4 mr-2" />
                  {t('ingredientReport.submit')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
  return createPortal(modalContent, document.body);
}
